import { useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { clock, parseClock, redoSection } from "@/lib/voiceSongsApi";
import { safeApiError } from "@/lib/safeErrorText";

const inp = "w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-[13px] text-white placeholder:text-white/30 focus:border-violet-300/50 focus:outline-none";
const lbl = "mb-1 mt-4 block font-mono text-[10px] uppercase tracking-[0.16em] text-white/50";

/** Regenerate 6–60 seconds of a Create Music song with new (or the same) lyrics — the original stays. */
export default function RedoSectionDialog({ open, onClose, song, duration, onStarted }) {
  const [start, setStart] = useState("0:30");
  const [end, setEnd] = useState("0:50");
  const [part, setPart] = useState("");
  const [full, setFull] = useState(song.lyrics || "");
  const [style, setStyle] = useState(song.style || "");
  const [busy, setBusy] = useState(false);
  const a = parseClock(start);
  const b = parseClock(end);
  const len = b - a;
  const problem = Number.isNaN(a) || Number.isNaN(b) ? "Write times like 1:05."
    : len < 6 || len > 60 ? "The part must be 6 to 60 seconds long."
    : duration && b > duration + 0.5 ? `The song ends at ${clock(duration)}.`
    : duration && len > duration / 2 ? "At most half of the song at once." : !part.trim() ? "Write the lyrics for this part." : !full.trim() ? "Add the song's full lyrics." : null;
  const go = async () => {
    setBusy(true);
    try {
      await redoSection({ song_id: song.id, start_s: a, end_s: b, section_lyrics: part.trim(), full_lyrics: full.trim(), style: style.trim() || null });
      toast.success("Re-doing that part…", { description: "Usually 2–4 minutes. The new version is saved as a new song." });
      onStarted(); onClose();
    } catch (e) { toast.error("Couldn't start", { description: safeApiError(e) }); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent data-testid="redo-section-dialog" className="max-h-[90vh] max-w-lg overflow-y-auto border-white/10 bg-[#0b0e1c] text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><RefreshCw className="h-4 w-4 text-violet-300" /> Re-do one part</DialogTitle>
          <DialogDescription className="text-white/60">Pick the part (6–60 seconds), write what should be sung there, and the music engine re-makes just that part. Uses ELPM credits.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lbl} htmlFor="rs-start">From</label><input id="rs-start" data-testid="redo-section-start" className={inp} value={start} onChange={(e) => setStart(e.target.value)} placeholder="1:05" /></div>
          <div><label className={lbl} htmlFor="rs-end">To</label><input id="rs-end" data-testid="redo-section-end" className={inp} value={end} onChange={(e) => setEnd(e.target.value)} placeholder="1:30" /></div>
        </div>
        <p data-testid="redo-section-length" className="mt-1.5 text-[11px] text-white/45">{Number.isNaN(len) ? "" : `${Math.max(0, Math.round(len))} seconds`}{duration ? ` · song length ${clock(duration)}` : ""}</p>
        <label className={lbl} htmlFor="rs-part">Lyrics for this part</label>
        <textarea id="rs-part" data-testid="redo-section-lyrics" rows={4} className={inp} value={part} onChange={(e) => setPart(e.target.value)} placeholder="The lines that should be sung in this part" />
        <label className={lbl} htmlFor="rs-full">Full lyrics after the change</label>
        <textarea id="rs-full" data-testid="redo-section-full" rows={6} className={inp} value={full} onChange={(e) => setFull(e.target.value)} />
        <label className={lbl} htmlFor="rs-style">Style</label>
        <input id="rs-style" data-testid="redo-section-style" className={inp} value={style} onChange={(e) => setStyle(e.target.value)} />
        {problem ? <p data-testid="redo-section-problem" className="mt-3 text-[12px] text-amber-200">{problem}</p> : null}
        <button type="button" data-testid="redo-section-submit" onClick={go} disabled={busy || Boolean(problem)}
          className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-violet-300 py-2.5 text-[13px] font-semibold text-black disabled:opacity-40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Re-do this part
        </button>
      </DialogContent>
    </Dialog>
  );
}
